import { z } from 'zod';
import { jsonPost } from '../json-request';
import type { ValidatedApiClient } from '../types/api-client';

export const rewardedAdLaunchSchema = z.object({
    url: z.url(),
    gamUnit: z.string().min(1),
    attemptId: z.uuid(),
    rewardBoosts: z.number().int().positive(),
    minimumViewSeconds: z.number().int().min(5).max(120),
});

export const rewardedAdAttemptSchema = z.object({
    id: z.uuid(),
    state: z.enum([
        'issued',
        'admitted',
        'showing',
        'completed',
        'awarded',
        'expired',
        'closed',
        'no-fill',
        'error',
        'canceled',
    ]),
    rewardBoosts: z.number().int().positive(),
    remainingBoosts: z.number().int().nonnegative().nullable(),
    houseAd: z
        .object({
            url: z.url().refine(url => new URL(url).protocol === 'https:'),
            durationSeconds: z.number().int().min(30).max(240),
        })
        .nullable()
        .optional(),
});

export const rewardedAdProgressSchema = z.object({
    id: z.uuid(),
    completedAds: z.number().int().nonnegative(),
    requiredAds: z.number().int().positive(),
    rewardBoosts: z.number().int().positive(),
    claimed: z.boolean(),
    resetsAt: z.iso.datetime({ offset: true }),
    remainingBoosts: z.number().int().nonnegative(),
});

export type RewardedAdProgress = z.infer<typeof rewardedAdProgressSchema>;
export type RewardedHouseAd = NonNullable<z.infer<typeof rewardedAdAttemptSchema>['houseAd']>;

export type RewardedAdAttempt = z.infer<typeof rewardedAdAttemptSchema>;
export type RewardedAdAttemptEvent = 'shown' | 'reward' | 'closed' | 'no-fill' | 'error' | 'canceled';

export const createRewardedAdsApi = ({ validatedFetch }: ValidatedApiClient) => ({
    progress: () => validatedFetch(rewardedAdProgressSchema, '/v3/rewarded_ads/progress', new URLSearchParams()),
    launch: (host: 'app' | 'dev_app') =>
        validatedFetch(rewardedAdLaunchSchema, '/v3/rewarded_ads/launch', new URLSearchParams(), jsonPost({ host })),
    record: (id: string, event: RewardedAdAttemptEvent) =>
        validatedFetch(
            rewardedAdAttemptSchema,
            `/v3/rewarded_ads/${encodeURIComponent(id)}/events`,
            new URLSearchParams(),
            jsonPost({ event }),
        ),
});

export type RewardedAdsApi = ReturnType<typeof createRewardedAdsApi>;
